import React, { useEffect, useState } from 'react';
import {
  Box,
  Datatable,
  ExpandableFilterGroup,
  Loading,
  Pagesection,
  SearchBar,
} from '@ktag/styleguide/dist/react';
import { FormattedCandidate } from 'lib/CandidatePortraits/CandidatePortraits';
import { useTranslation } from 'react-i18next';
import { Filter } from '@ktag/styleguide/dist/components/filter/react/ExpandableFilter/expandableFilter.types';
import useGetTableSortFunction from '../utils/useGetTableSortFunction';
import { useGetCandidatesTableData } from '../utils/useGetCandidatesTableData';

interface Props {
  candidates: FormattedCandidate[];
  electedCandidatesCanton: FormattedCandidate[];
  isBiProportional: boolean;
}

const ProportionalElectedTable = ({
  candidates,
  electedCandidatesCanton,
  isBiProportional,
}: Props) => {
  const { t } = useTranslation('wap');

  const [data, updateData] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedLists, setSelectedLists] = useState<string[]>([]);
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([]);

  const { tableData, distinctDistricts, distinctLists } = useGetCandidatesTableData(
    candidates || electedCandidatesCanton,
    {
      showElectedColumn: false,
      showOnlyElected: true,
      showCountingCircleColumn: isBiProportional,
    },
    {
      search,
      selectedLists,
      selectedDistricts,
    },
  );

  const handlePageSort = useGetTableSortFunction(data, updateData);

  useEffect(() => {
    updateData(tableData);
  }, [tableData?.body?.rows?.length]);

  if (!data) return <Loading size="big" />;

  const listOptions = distinctLists
    ?.map((list) => ({
      label: list,
      value: list,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const filterOptions: Filter[] = [
    {
      label: t('proportional_district'),
      name: t('proportional_district'),
      type: 'multiselect',
      options: distinctDistricts?.map((district) => ({
        label: district,
        value: district,
      })),
      onChange: (values) => {
        setSelectedDistricts(values);
      },
    },
    {
      label: t('list'),
      name: t('list'),
      type: 'multiselect',
      options: listOptions,
      onChange: (values) => {
        setSelectedLists(values);
      },
    },
  ];

  if (isBiProportional) {
    return (
      <>
        <Pagesection additionalClassNames="proportionalelectiondetails__elected">
          <SearchBar
            search={search}
            setSearch={setSearch}
            placeholder={t('elected_search')}
          />
          <ExpandableFilterGroup filters={filterOptions} />
          <Box
            type="information"
            heading={t('biproportional_candidates_info_box_title')}
            additionalClassNames="proportionalelectiondetails__candidates-info-box"
          >
            {t('biproportional_candidates_info_box_content')}
          </Box>
        </Pagesection>
        <Pagesection type="medium">
          <Datatable data={data} scrollable handlePageSort={handlePageSort} />
        </Pagesection>
      </>
    );
  }

  return (
    <Pagesection type="medium">
      <Datatable data={data} scrollable handlePageSort={handlePageSort} />
    </Pagesection>
  );
};

export default ProportionalElectedTable;
